/* eslint-disable camelcase */
import axios from 'axios';
import {
  canRetryWithStoredToken,
  hasImpersonationFlag,
  isImpersonationAuthorization,
} from '../../impersonation/impersonationSession';

const ACCESS_TOKEN_KEY = 'jwt_access_token';

// Requests made with `responseType: 'blob'` (images, files) get their error body
// as a Blob too, which hides the backend `statusCode` (e.g. 4001 for an expired
// token). Decode it so such errors are handled like any other JSON error.
const parseBlobError = async (data) => {
  if (typeof Blob === 'undefined' || !(data instanceof Blob) || !data.type.includes('json')) {
    return data;
  }
  try {
    return JSON.parse(await data.text());
  } catch {
    return data;
  }
};

class AxiosManagerService {
  #instances = new Map();

  #mappedHandlers = new Map();

  // A single in-flight refresh shared by every request that hits an expired
  // token, so they all wait for it and retry instead of refreshing in parallel.
  #refreshPromise = null;

  bindHttpErrorsHandlers(mappedHandlers) {
    this.#mappedHandlers = mappedHandlers;
  }

  getInstance(host, path) {
    const baseURL = `${host}/${path}`;

    if (!this.#instances.has(host)) {
      const instance = axios.create({ baseURL });
      this.#instances.set(host, instance);
      return instance;
    }

    const parentInstance = this.#instances.get(host);
    const instance = axios.create({ baseURL });

    instance.defaults.headers = parentInstance.defaults.headers;
    this.setInterceptors(instance);

    return instance;
  }

  setHeader(name, value) {
    this.#instances.forEach((instance) => {
      instance.defaults.headers.common[name] = value;
    });
  }

  setInterceptors(axiosInstance) {
    axiosInstance.interceptors.response.use(
      (res) => res,
      async (err) => {
        const originalRequest = err.config;
        if (err.response) {
          err.response.data = await parseBlobError(err.response.data);
        }
        const statusCode = err.response?.data?.statusCode || err.response?.status;
        if (!statusCode) throw err;

        const callback = this.#mappedHandlers.get(statusCode);

        if (statusCode === 4001 && originalRequest && !originalRequest.isRetryAfterRefresh) {
          const retry = (accessToken) =>
            axiosInstance.request({
              ...originalRequest,
              isRetryAfterRefresh: true,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${accessToken}`,
              },
            });

          const sentAuthorization = originalRequest.headers?.Authorization;

          // A request sent as a read-only admin session is never retried, by
          // any path: whatever token exists now belongs to another session.
          // The handler still runs so the read-only session ends.
          if (isImpersonationAuthorization(sentAuthorization)) {
            if (!this.#refreshPromise) {
              this.#refreshPromise = Promise.resolve()
                .then(() => callback?.())
                .finally(() => {
                  this.#refreshPromise = null;
                });
            }
            await this.#refreshPromise.catch(() => {});

            throw err;
          }

          // The token was already refreshed while this request was in flight.
          // localStorage is shared by every tab, so only reuse the stored token
          // when it belongs to the same identity, and never in a read-only
          // admin session (its expiry must end it, not switch it).
          const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
          if (
            !this.#refreshPromise &&
            canRetryWithStoredToken({
              sentAuthorization,
              storedToken: currentToken,
              tabImpersonating: hasImpersonationFlag(),
            })
          ) {
            return retry(currentToken);
          }

          if (!this.#refreshPromise) {
            this.#refreshPromise = Promise.resolve(callback()).finally(() => {
              this.#refreshPromise = null;
            });
          }

          const { access_token } = await this.#refreshPromise;

          return retry(access_token);
        }

        if (callback) {
          await callback?.(err);
        }

        throw err;
      }
    );
  }

  stringifyError = (err) => err.response?.data?.error || err.message;
}

export const AxiosManager = new AxiosManagerService();
